import React from 'react';
import { useTheme } from '../../hooks/useTheme';

class RouteBoundary extends React.Component<
  {
    children: React.ReactNode;
    routeName: string;
    textColor: string;
    borderColor: string;
    backgroundColor: string;
  },
  { hasError: boolean; message: string }
> {
  constructor(props: {
    children: React.ReactNode;
    routeName: string;
    textColor: string;
    borderColor: string;
    backgroundColor: string;
  }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown route render error';
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown) {
    console.error(`[ProfileRouteBoundary.web:${this.props.routeName}]`, error);
  }

  render() {
    if (!this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            minHeight: 0,
            position: 'relative',
          }}
        >
          {this.props.children}
        </div>
      );
    }

    return (
      <div
        style={{
          height: '100%',
          padding: 20,
          backgroundColor: this.props.backgroundColor,
        }}
      >
        <div
          style={{
            border: `1px solid ${this.props.borderColor}`,
            borderRadius: 14,
            padding: 14,
            display: 'grid',
            gap: 6,
          }}
        >
          <div style={{ color: this.props.textColor, fontSize: 16, fontWeight: 700 }}>
            Screen failed to render
          </div>
          <div style={{ color: this.props.textColor, fontSize: 13 }}>{this.props.routeName}</div>
          <div style={{ color: this.props.textColor, fontSize: 13, lineHeight: '18px' }}>
            {this.state.message}
          </div>
        </div>
      </div>
    );
  }
}

export function ProfileRouteBoundary({
  routeName,
  children,
}: {
  routeName: string;
  children: React.ReactNode;
}) {
  const { theme } = useTheme();

  return (
    <RouteBoundary
      routeName={routeName}
      textColor={theme.text}
      borderColor={theme.borderSubtle}
      backgroundColor={theme.background}
    >
      {children}
    </RouteBoundary>
  );
}
